import { useCallback, useEffect, useRef, useState } from "react";
import { useApiConfig } from "@/providers/ApiConfigProvider";
import { useMode } from "@/providers/ModeProvider";
import type { GraphQLCursorConnection } from "@/lib/graphql-pagination";

// --- Types ---

export interface CursorPaginationResult<T> {
  items: T[];
  isLoading: boolean;
  isFetchingMore: boolean;
  hasMore: boolean;
  loadMore: () => void;
  reset: () => void;
}

export interface CursorPaginationConfig<TNode, TItem> {
  /** Page size for each fetch */
  pageSize: number;
  /** Build mock data for demo mode */
  buildMockData: () => TItem[];
  /** Execute the GraphQL query and return the connection */
  executeQuery: (
    apiConfig: ReturnType<typeof useApiConfig>,
    afterCursor: string | null,
    signal: AbortSignal,
  ) => Promise<GraphQLCursorConnection<TNode>>;
  /** Map a GraphQL node to the domain item */
  mapNode: (node: TNode) => TItem;
  /** Optional post-processing of mapped items (e.g. sorting) */
  postProcess?: (items: TItem[]) => TItem[];
}

// --- Hook ---

/**
 * Generic cursor-based "load more" pagination hook.
 * Encapsulates the shared state management, fetching, load-more, and reset logic
 * used by audit log, recent activity, and customer search hooks.
 */
export function useCursorPagination<TNode, TItem>(
  config: CursorPaginationConfig<TNode, TItem>,
  deps: unknown[],
): CursorPaginationResult<TItem> {
  const apiConfig = useApiConfig();
  const { isDemo } = useMode();
  const [items, setItems] = useState<TItem[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const endCursorRef = useRef<string | null>(null);
  const mockPageRef = useRef(1);

  const fetchData = useCallback(
    async (afterCursor: string | null, append: boolean) => {
      if (isDemo) {
        const allMock = config.buildMockData();
        if (append) {
          mockPageRef.current += 1;
        } else {
          mockPageRef.current = 1;
        }
        const end = mockPageRef.current * config.pageSize;
        const sliced = allMock.slice(0, end);
        setItems(sliced);
        setHasMore(end < allMock.length);
        setIsLoading(false);
        setIsFetchingMore(false);
        return;
      }

      abortControllerRef.current?.abort();
      const controller = new AbortController();
      abortControllerRef.current = controller;

      try {
        const connection = await config.executeQuery(apiConfig, afterCursor, controller.signal);
        let mapped = connection.edges.map((x) => config.mapNode(x.node));
        if (config.postProcess) {
          mapped = config.postProcess(mapped);
        }

        if (append) {
          setItems((prev) => [...prev, ...mapped]);
        } else {
          setItems(mapped);
        }

        endCursorRef.current = connection.pageInfo.endCursor;
        setHasMore(connection.pageInfo.hasNextPage);
      } catch (error: unknown) {
        if ((error as Error)?.name === "AbortError") return;
        if (!append) {
          setItems([]);
        }
        setHasMore(false);
      } finally {
        if (!controller.signal.aborted) {
          setIsLoading(false);
          setIsFetchingMore(false);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [apiConfig, isDemo, ...deps],
  );

  useEffect(() => {
    endCursorRef.current = null;
    mockPageRef.current = 1;
    setIsLoading(true);
    setIsFetchingMore(false);
    fetchData(null, false);
  }, [fetchData]);

  const loadMore = useCallback(() => {
    if (isFetchingMore || !hasMore) return;
    setIsFetchingMore(true);
    fetchData(endCursorRef.current, true);
  }, [isFetchingMore, hasMore, fetchData]);

  const reset = useCallback(() => {
    abortControllerRef.current?.abort();
    endCursorRef.current = null;
    mockPageRef.current = 1;
    setItems([]);
    setHasMore(false);
    setIsLoading(false);
    setIsFetchingMore(false);
  }, []);

  return { items, isLoading, isFetchingMore, hasMore, loadMore, reset };
}
